import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';
import { getContext } from 'configs/context';

import { ALLOWED_LIMIT_FILES, BATCH_ERROR } from 'pages/Batch/configs';

import { BatchDataStatus } from 'configs/constants';

import type { IBatchData } from 'types';
import type { IBatchFileData } from 'types/store';

import uploadService from 'utils/images/uploadPhoto';

const INTERNALS_KEY = 'batch_data';
const saveStateParams = {
  permanent: true,
  rerender: false,
  replay: false,
};

class BatchDataService {
  private uploadingCount = 0;

  private loadingData: string[] = [];

  private queue = Promise.resolve();

  // Method logic for preparing data for Initial upload step
  private async prepareInitialUploadedData(item: IBatchFileData) {
    await this.incrementUploadingCount(item.key);
    try {
      const { source, result } = item;
      const { blob: sourceBlob, ...restSource } = source;
      const { blob: resultBlob, ...restResult } = result;
      const sourceUrl = sourceBlob ? await uploadService.upload(sourceBlob) : null;

      if (!sourceUrl) return;

      return {
        ...item,
        source: {
          ...restSource,
          src: sourceUrl,
        },
        result: {
          ...restResult,
          src: sourceUrl,
        },
      };
    } finally {
      await this.decrementUploadingCount(item.key);
    }
  }

  // Method logic for preparing data for the Apply step
  private async prepareAppliedData(item: IBatchFileData) {
    await this.incrementUploadingCount(item.key);
    try {
      const { result } = item;
      const { src, ...restResult } = result;

      const url = new URL(src);
      let resultUrl;
      if (url.protocol === 'blob:') {
        const loadedImage = await fetch(src);
        const resultBlob = await loadedImage.blob();
        resultUrl = resultBlob ? await uploadService.upload(resultBlob) : null;

        if (!resultUrl) return;
      } else {
        resultUrl = url.toString();
      }

      return {
        ...item,
        result: {
          ...restResult,
          src: resultUrl,
        },
      };
    } finally {
      await this.decrementUploadingCount(item.key);
    }
  }

  getAllData() {
    const { getState } = getContext().internals;
    const state = getState(saveStateParams);
    const data = (state[INTERNALS_KEY] || []) as IBatchData[];
    return data;
  }

  async getData(itemKey: string): Promise<IBatchData | null> {
    const maxWaitTimeMs = 60_000; // 60 seconds
    const waitIntervalMs = 50;

    return new Promise<IBatchData | null>((resolve, reject) => {
      let waitedTime = 0;

      const checkUploading = () => {
        const foundItem = this.getAllData().find(i => i && i.key === itemKey);

        if (!this.loadingData.includes(itemKey)) {
          if (!foundItem) {
            errorLogger.leaveBreadcrumb(
              'DataService',
              {
                itemKey,
                allData: this.getAllData(),
                loadingData: this.loadingData,
                waitedTime,
              },
              'state',
            );
          }
          resolve(foundItem ?? null);
          return;
        }

        if (waitedTime >= maxWaitTimeMs) {
          reject(new Error(BATCH_ERROR.uploadFailed));
          return;
        }

        waitedTime += waitIntervalMs;
        setTimeout(checkUploading, waitIntervalMs);
      };

      checkUploading();
    });
  }

  private async addData(item: IBatchData) {
    const data = await this.prepareInitialUploadedData(item);

    if (!data) return;

    const { getState, setState } = getContext().internals;
    const state = getState(saveStateParams);
    state[INTERNALS_KEY] = [...this.getAllData(), data];
    setState(state, saveStateParams);
  }

  async addMultipleData(data: IBatchFileData[]) {
    const currentData = this.getAllData();
    const allowedLimit = ALLOWED_LIMIT_FILES - (currentData?.length || 0);
    const filteredData = data.slice(0, allowedLimit);

    filteredData.forEach(file => {
      if (!this.loadingData.includes(file.key)) {
        this.loadingData.push(file.key);
      }
    });

    filteredData.forEach(file => this.addData(file));
  }

  async putMultipleData(data: IBatchData[], selectedPhotoIds: string[]) {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      const allData = this.getAllData();

      data.forEach(item => {
        if (selectedPhotoIds.includes(item.key) && !this.loadingData.includes(item.key)) {
          this.loadingData.push(item.key);
        }
      });

      // Create an array to store item processing promises
      const preparedItemsPromises = data.map(async item => {
        try {
          const index = allData.findIndex(({ key }) => key === item.key);
          const storageItem = allData[index];

          if (selectedPhotoIds.includes(item.key)) {
            const preparedApplyData = await this.prepareAppliedData(item);
            if (!preparedApplyData) return null;
            return { ...preparedApplyData, source: { ...storageItem.source } };
          }

          return storageItem;
        } catch (error) {
          return null;
        }
      });

      const preparedItems = await Promise.all(preparedItemsPromises);

      // Filter out null values (indicating errors)
      state[INTERNALS_KEY] = preparedItems.filter(item => item !== null);
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  removeData(key: string) {
    try {
      const data = this.getAllData();
      const filteredData = data.filter(item => item && item.key !== key);

      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);

      state[INTERNALS_KEY] = filteredData;
      this.loadingData = this.loadingData.filter(k => k !== key);

      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  resetData(keysToReset: string[]) {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      const allData = this.getAllData();
      state[INTERNALS_KEY] = allData.map(storageItem => {
        if (storageItem && keysToReset.includes(storageItem.key)) {
          return {
            ...storageItem,
            result: storageItem.source,
          };
        }
        return storageItem;
      });
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  updateItem(itemKey: string, updates: Partial<IBatchData>) {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      state[INTERNALS_KEY] = this.getAllData().map(item => (item?.key === itemKey ? { ...item, ...updates } : item));
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  discardPendingData() {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      const allData = this.getAllData();
      state[INTERNALS_KEY] = allData.map(item => {
        if (!item) return item;
        return {
          ...item,
          pendingResult: undefined,
          asyncJob: undefined,
          lastToolAction: undefined,
          status: BatchDataStatus.PENDING,
        };
      });
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  deleteAllData() {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      state[INTERNALS_KEY] = [];
      this.loadingData = [];
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  replaceAllData(items: IBatchData[]) {
    try {
      const { getState, setState } = getContext().internals;
      const state = getState(saveStateParams);
      state[INTERNALS_KEY] = items;
      setState(state, saveStateParams);
    } catch (e) {
      errorLogger.trigger(e as Error, BUGSNAG_STATUSES.ERROR);
    }
  }

  private enqueue(fn: () => void) {
    this.queue = this.queue.then(fn, fn);
    return this.queue;
  }

  private incrementUploadingCount(key: string) {
    return this.enqueue(() => {
      if (!this.loadingData.includes(key)) {
        this.loadingData.push(key);
      }
      this.uploadingCount++;
    });
  }

  private decrementUploadingCount(key: string) {
    return this.enqueue(() => {
      this.loadingData = this.loadingData.filter(k => k !== key);
      this.uploadingCount--;
    });
  }
}

export default new BatchDataService();
